import Route from 'cio/routes/base';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import { scheduleOnce } from '@ember/runloop';
import { action } from '@ember/object';
import INTEGRATIONS_METADATA, { StaticIntegration } from 'cio/utils/integrations/static-integrations';
import { NAMES } from 'cio/utils/ad-audiences';
import WindowMessengerService from 'cio/services/window-messenger';
import IntegrationsPublicTypeController from './controller';
import urlBuilder from 'cio/utils/url-builder';
import IntegrationModel from 'cio/models/integration';
import Transition from '@ember/routing/-private/transition';
import DS from 'ember-data';

export default class IntegrationsPublicType extends Route {
  @service router!: RouterService;
  @service windowMessenger!: WindowMessengerService;
  @service store!: DS.Store;

  crumb() {
    let { type } = this.paramsFor('integrations-public.type') as Record<string, string>;
    let displayName = (NAMES as Record<string, string>)[type] || type;

    return {
      // This reverses the serialization process on the back end
      name: type ? displayName.replace(/_/g, ' ') : '',
      path: 'integrations-public.type',
      model: type,
    };
  }

  async model() {
    let { type } = this.paramsFor('integrations-public.type') as Record<string, string>;
    let integrations = await this.store.findAll('integration');
    let allIntegrations = [...Object.values(INTEGRATIONS_METADATA), ...integrations.toArray()];
    let integration = allIntegrations.find(
      (integration) => integration.id === type || integration.dynamicSegment === type,
    );

    if (!integration) {
      // shouldn't ever happen in real-life scenarios
      return;
    }

    this.windowMessenger.send('metadata-update', {
      title: integration.name,
      description: integration.overview,
    });

    return integration;
  }

  afterModel(model: StaticIntegration | IntegrationModel | undefined, _: any) {
    if (!model) {
      this.router.transitionTo('integrations-public.index');
      this.windowMessenger.send('page-change', {
        path: urlBuilder('/integrations', {}),
      });
    }
  }

  setupController(controller: IntegrationsPublicTypeController, model: Record<string, any>, transition: Transition) {
    super.setupController(controller, model, transition);

    window.addEventListener('resize', controller.handleResize);
    scheduleOnce('afterRender', controller, controller.handleResize);
  }

  resetController(controller: IntegrationsPublicTypeController, isExiting: boolean, transition: Transition) {
    super.resetController(controller, isExiting, transition);

    if (isExiting) {
      window.removeEventListener('resize', controller.handleResize);
    }
  }

  @action
  willTransition(transition: any) {
    if (transition.to.name === 'integrations-public.index') {
      this.windowMessenger.send('page-change', {
        path: urlBuilder('/integrations', transition.to.queryParams),
      });
    }
  }
}
